/**
 * Gelato Product UID Builder
 * Constructs the correct Gelato productUid based on orientation, size, frame, and color
 */

import type { Orientation, FormatSize } from './imageFormats';
import type { FrameType } from './frameOptions';

export interface GelatoProductParams {
  orientation: Orientation;
  size: FormatSize;
  frameType: FrameType;
  frameColor?: string;
}

// Gelato dimension strings by orientation and size
// Format: {width}x{height}-mm-{inches}
export const GELATO_DIMENSIONS: Record<string, string> = {
  // Portrait (height > width)
  'portrait_small': '150x200-mm-6x8-inch',
  'portrait_medium': '300x400-mm-12x16-inch',
  'portrait_large': '450x600-mm-18x24-inch',
  // Landscape — Gelato expects portrait order (small x large), orientation set via _hor suffix
  'landscape_small': '150x200-mm-6x8-inch',
  'landscape_medium': '300x400-mm-12x16-inch',
  'landscape_large': '450x600-mm-18x24-inch',
  // Square
  'square_small': '300x300-mm-12x12-inch',
  'square_medium': '400x400-mm-16x16-inch',
  'square_large': '500x500-mm-20x20-inch',
};

// Gelato plexiglass dimension suffixes (some differ from print size)
// For most sizes, plexiglass matches print size, but some have special formats
export const GELATO_PLEXIGLASS_DIMENSIONS: Record<string, string> = {
  // Portrait
  'portrait_small': '150x200-mm-6x8-inch',
  'portrait_medium': '300x400-mm-12x16-inch',
  'portrait_large': '450x600-mm-18x24-inch',
  // Landscape — Gelato expects portrait order (small x large)
  'landscape_small': '150x200-mm-6x8-inch',
  'landscape_medium': '300x400-mm-12x16-inch',
  'landscape_large': '450x600-mm-18x24-inch',
  // Square
  'square_small': '300x300-mm-12x12-inch',
  'square_medium': '400x400-mm-16x16-inch',
  'square_large': '500x500-mm-20x20-inch',
};

// Mapping frontend color IDs to Gelato color strings
export const GELATO_WOOD_COLORS: Record<string, string> = {
  'black': 'black',
  'white': 'white',
  'natural': 'natural-wood',
  'dark': 'dark-wood',
};

// Gelato uses different identifiers for flat and framed 200 gsm products.
const FLAT_PAPER_SPEC = '200-gsm-80lb-uncoated';
const FRAMED_PAPER_SPEC = '200-gsm-uncoated';

// Color mode (constant)
const COLOR_MODE = '4-0';

/**
 * Get orientation suffix for Gelato productUid
 * portrait/square = ver (vertical), landscape = hor (horizontal)
 */
function getOrientationSuffix(orientation: Orientation): string {
  return orientation === 'landscape' ? 'hor' : 'ver';
}

/**
 * Build productUid for flat (unframed) poster
 * Format: flat_{dimensions}_{paper}_{colorMode}_{orientation}
 */
function buildFlatProductUid(orientation: Orientation, size: FormatSize): string {
  const key = `${orientation}_${size}`;
  const dimensions = GELATO_DIMENSIONS[key];
  const orientationSuffix = getOrientationSuffix(orientation);

  return `flat_${dimensions}_${FLAT_PAPER_SPEC}_${COLOR_MODE}_${orientationSuffix}`;
}

/**
 * Build productUid for wood framed poster
 * Format: framed_poster_mounted_{dimensions}_{color}_wood_w12xt22-mm_plexiglass_{dimensions}_{paper}_{colorMode}_{orientation}
 */
function buildWoodFrameProductUid(
  orientation: Orientation,
  size: FormatSize,
  colorId: string
): string {
  const key = `${orientation}_${size}`;
  const dimensions = GELATO_DIMENSIONS[key];
  const plexiglassDimensions = GELATO_PLEXIGLASS_DIMENSIONS[key];
  const gelatoColor = GELATO_WOOD_COLORS[colorId] || 'black';
  const orientationSuffix = getOrientationSuffix(orientation);

  return `framed_poster_mounted_${dimensions}_${gelatoColor}_wood_w12xt22-mm_plexiglass_${plexiglassDimensions}_${FRAMED_PAPER_SPEC}_${COLOR_MODE}_${orientationSuffix}`;
}

/**
 * Main function to build Gelato productUid based on all parameters
 */
export function buildGelatoProductUid(params: GelatoProductParams): string {
  const { orientation, size, frameType, frameColor } = params;

  switch (frameType) {
    case 'none':
      return buildFlatProductUid(orientation, size);

    case 'wood':
      return buildWoodFrameProductUid(orientation, size, frameColor || 'black');

    default:
      // Fallback to flat poster
      return buildFlatProductUid(orientation, size);
  }
}

/**
 * Get the dimension key for lookup
 */
export function getDimensionKey(orientation: Orientation, size: FormatSize): string {
  return `${orientation}_${size}`;
}

/**
 * Check if a dimension configuration exists
 */
export function hasGelatoDimension(orientation: Orientation, size: FormatSize): boolean {
  const key = getDimensionKey(orientation, size);
  return key in GELATO_DIMENSIONS;
}
